import React, { useState, useEffect } from "react";
import { TablePagination } from "@mui/material";
import { createProject, getAllProjects, updateProject, deleteProject } from "../../Api/projectApi";
import toast from "react-hot-toast";
import { FaSpinner, FaClock, FaTimes } from "react-icons/fa";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,500;0,9..144,600;0,9..144,700;0,9..144,800;1,9..144,500;1,9..144,600&family=IBM+Plex+Mono:wght@500;600;700&family=Inter:wght@400;500;600;700&display=swap');

  .dash-root {
    --ink:#101826; --paper:#FFFFFF; --surface:#FFFFFF; --surface-alt:#FFFFFF;
    --brass:#2BB5CE; --brass-soft:#E6F7FA; --brass-deep:#1A869B;
    --teal:#2BB5CE; --teal-soft:#E6F7FA;
    --plum:#1E293B; --plum-soft:#F1F5F9;
    --rose:#B23A48; --rose-soft:#F6DEDF;
    --slate:#6B7686; --hairline:rgba(16,24,38,0.11);
    font-family:'Inter',sans-serif; min-height:100vh; color:var(--ink);
    background-color:#FFFFFF;
  }
  .dash-root * { box-sizing:border-box; }

  .eyebrow {
    font-family:'IBM Plex Mono',monospace; font-size:11px; font-weight:600;
    letter-spacing:0.16em; text-transform:uppercase; color:var(--brass-deep);
    display:flex; align-items:center; gap:8px; margin-bottom:8px;
  }
  .eyebrow::before { content:''; width:16px; height:1px; background:var(--brass); display:inline-block; }

  .section-title { font-family:'Fraunces',serif; font-style:italic; font-size:28px; font-weight:600; color:var(--ink); margin-bottom:4px; letter-spacing:-0.01em; }
  .section-sub   { font-size:13px; color:var(--slate); margin-bottom:26px; }

  .dash-table { width:100%; border-collapse:separate; border-spacing:0; }
  .dash-table thead th {
    background:var(--surface-alt); color:var(--slate); font-family:'IBM Plex Mono',monospace; font-size:10.5px; font-weight:600;
    letter-spacing:0.12em; text-transform:uppercase; padding:14px 18px;
    border-bottom:1px solid var(--hairline);
  }
  .dash-table thead th:first-child { border-radius:14px 0 0 0; }
  .dash-table thead th:last-child  { border-radius:0 14px 0 0; }
  .dash-table tbody tr { transition:background 0.13s ease; }
  .dash-table tbody tr:hover td { background:var(--surface-alt); }
  .dash-table tbody td { padding:13px 18px; border-bottom:1px solid #F1F3F8; color:var(--ink); font-size:14px; background:var(--surface); }
  .dash-table tbody tr:last-child td { border-bottom:none; }
  .dash-table tbody tr:last-child td:first-child { border-radius:0 0 0 14px; }
  .dash-table tbody tr:last-child td:last-child  { border-radius:0 0 14px 0; }

  .input-field {
    width: 100%;
    background: var(--surface);
    border: 1px solid var(--hairline);
    border-radius: 8px;
    padding: 11px 14px;
    font-size: 13px;
    color: var(--ink);
    font-family: 'Inter', sans-serif;
    outline: none;
    transition: border-color 0.15s, box-shadow 0.15s;
  }
  .input-field:focus {
    border-color: var(--brass);
    box-shadow: 0 0 0 3px var(--brass-soft);
  }
  .input-field:disabled {
    background: var(--surface-alt);
    color: var(--slate);
    cursor: not-allowed;
  }
  
  .modal-box {
    background: #ffffff;
    border: 1px solid var(--hairline);
    border-radius: 6px 6px 20px 20px;
    box-shadow: 0 24px 64px rgba(16,24,38,0.18);
    overflow: hidden;
    width: 100%;
  }
  .modal-header {
    font-family: 'Fraunces', serif;
    font-style: italic;
    font-weight: 600;
    font-size: 19px;
    color: var(--ink);
    padding: 18px 24px;
    border-bottom: 1px solid var(--hairline);
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .modal-body {
    padding: 24px;
  }
  .modal-footer {
    border-top: 1px solid var(--hairline);
    padding: 14px 24px;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
  }
`;

const Project = () => {
  const [open, setOpen] = useState(false);
  const [projectList, setProjectList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    setPage(0);
  }, [projectList]);

  // Modal Mode: "create" | "edit" | "view"
  const [modalMode, setModalMode] = useState("create");
  const [activeId, setActiveId] = useState(null);

  // Form State
  const [selectedProject, setSelectedProject] = useState("");
  const [customProject, setCustomProject] = useState("");
  const [selectedProgram, setSelectedProgram] = useState("");
  const [customProgram, setCustomProgram] = useState("");
  const [courseDuration, setCourseDuration] = useState("");

  const staticProjects = {
    "Shriram Finance": [
      "One Day Refresher Program",
      "Pre Licensing LMV Training Program",
      "Pre Licensing HMV Program",
    ],
    Mahindra: ["E Rickshaw Training Program"],
    Linde: [
      "One Day Refresher Program",
      "Hazardous (Hydrogen Tanker) Training Program",
    ],
  };

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    setLoading(true);
    try {
      const res = await getAllProjects();
      if (res && res.success && Array.isArray(res.data)) {
        setProjectList(res.data);
      } else if (Array.isArray(res)) {
        setProjectList(res);
      } else if (res && Array.isArray(res.data)) {
        setProjectList(res.data);
      } else {
        setProjectList([]);
      }
    } catch (err) {
      console.error("Error loading projects:", err);
      toast.error("Failed to load projects list");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setModalMode("create");
    setActiveId(null);
    setSelectedProject("");
    setCustomProject("");
    setSelectedProgram("");
    setCustomProgram("");
    setCourseDuration("");
    setOpen(true);
  };

  const handleOpenEdit = (p) => {
    setModalMode("edit");
    setActiveId(p._id);
    
    // Check if project exists in presets
    if (staticProjects[p.projectName]) {
      setSelectedProject(p.projectName);
      // Check if program exists in presets
      if (staticProjects[p.projectName].includes(p.programName)) {
        setSelectedProgram(p.programName);
        setCustomProgram("");
      } else {
        setSelectedProgram("custom");
        setCustomProgram(p.programName);
      }
      setCustomProject("");
    } else {
      setSelectedProject("custom");
      setCustomProject(p.projectName);
      setSelectedProgram("custom");
      setCustomProgram(p.programName);
    }

    setCourseDuration(p.courseDuration || "");
    setOpen(true);
  };

  const handleOpenView = (p) => {
    setModalMode("view");
    setActiveId(p._id);
    
    if (staticProjects[p.projectName]) {
      setSelectedProject(p.projectName);
      if (staticProjects[p.projectName].includes(p.programName)) {
        setSelectedProgram(p.programName);
      } else {
        setSelectedProgram("custom");
        setCustomProgram(p.programName);
      }
    } else {
      setSelectedProject("custom");
      setCustomProject(p.projectName);
      setSelectedProgram("custom");
      setCustomProgram(p.programName);
    }

    setCourseDuration(p.courseDuration || "");
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this project program configuration?")) {
      try {
        const res = await deleteProject(id);
        if (res.success) {
          toast.success("Project program deleted successfully");
          loadProjects();
        } else {
          toast.error(res.message || "Failed to delete project program");
        }
      } catch (err) {
        console.error("Delete project error:", err);
        toast.error("Error deleting project program");
      }
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (modalMode === "view") return;
    
    const finalProjectName = selectedProject === "custom" ? customProject.trim() : selectedProject;
    const finalProgramName = selectedProgram === "custom" ? customProgram.trim() : selectedProgram;

    if (!finalProjectName) {
      toast.error("Project name is required");
      return;
    }
    if (!finalProgramName) {
      toast.error("Program name is required");
      return;
    }
    if (!courseDuration.trim()) {
      toast.error("Course duration is required");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        projectName: finalProjectName,
        programName: finalProgramName,
        courseDuration: courseDuration.trim()
      };
      
      let res;
      if (modalMode === "edit") {
        res = await updateProject(activeId, payload);
      } else {
        res = await createProject(payload);
      }

      if (res.success || res.data) {
        toast.success(modalMode === "edit" ? "Project updated successfully" : "Project program created successfully");
        setOpen(false);
        loadProjects();
      } else {
        toast.error(res.message || "Failed to save project");
      }
    } catch (err) {
      console.error("Save project error:", err);
      toast.error(err.response?.data?.message || "Error saving project");
    } finally {
      setSaving(false);
    }
  };

  const getAvailablePrograms = () => {
    if (!selectedProject || selectedProject === "custom") return [];
    return staticProjects[selectedProject] || [];
  };

  const paginatedProjects = projectList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <>
      <style>{styles}</style>
      <div className="dash-root" style={{ padding: "18px 18px" }}>
        
        {/* HEADER */}
        <div className="eyebrow">Settings · Projects</div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "26px" }}>
          <div>
            <div className="section-title">Projects & Programs</div>
            <div className="section-sub" style={{ marginBottom: 0 }}>Configure dynamic courses, center projects, and default training durations</div>
          </div>
          <button 
            onClick={handleOpenCreate}
            style={{
              margin: 0,
              background: "#2BB5CE",
              color: "#FFFFFF",
              border: "none",
              borderRadius: "8px",
              padding: "10px 18px",
              fontWeight: 700,
              fontSize: "13px",
              cursor: "pointer",
              boxShadow: "0 4px 14px rgba(43,181,206,0.32)",
              fontFamily: "'IBM Plex Mono', monospace"
            }}
          >
            + Create Program
          </button>
        </div>

        {/* PROJECTS TABLE */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400 gap-3">
            <FaSpinner className="animate-spin text-2xl text-[#2BB5CE]" />
            <p className="text-sm">Loading projects list...</p>
          </div>
        ) : projectList.length === 0 ? (
          <div className="text-center py-20 text-gray-450 border border-dashed border-gray-200 rounded-xl bg-slate-50/50">
            <p className="text-sm font-medium">No project configurations found.</p>
            <p className="text-xs mt-1">Click the button above to register your first training program.</p>
          </div>
        ) : (
          <div style={{ borderRadius: "6px 6px 16px 16px", overflow: "hidden", border: "1px solid var(--hairline)", boxShadow: "0 2px 12px rgba(16,24,38,0.06)" }}>
            <table className="dash-table">
              <thead>
                <tr>
                  <th style={{ width: "60px", textAlign: "left" }}>S.No</th>
                  <th style={{ textAlign: "left" }}>Project Name</th>
                  <th style={{ textAlign: "left" }}>Program Name</th>
                  <th style={{ textAlign: "center", width: "180px" }}>Course Duration</th>
                  <th style={{ textAlign: "center", width: "160px" }}>Created Date</th>
                  <th style={{ textAlign: "center", width: "200px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedProjects.map((p, index) => (
                  <tr key={p._id}>
                    {/* S.NO */}
                    <td style={{ fontFamily: "'IBM Plex Mono', monospace", fontWeight: 600, color: "var(--slate)" }}>
                      {page * rowsPerPage + index + 1}
                    </td>

                    {/* PROJECT NAME */}
                    <td style={{ fontWeight: 700, color: "var(--ink)" }}>
                      {p.projectName}
                    </td>

                    {/* PROGRAM NAME */}
                    <td>
                      {p.programName}
                    </td>

                    {/* COURSE DURATION */}
                    <td style={{ textAlign: "center" }}>
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-cyan-50 text-cyan-700 text-xs font-semibold rounded-full border border-cyan-100">
                        <FaClock className="text-[10px]" />
                        {p.courseDuration || "Not Set"}
                      </span>
                    </td>

                    {/* CREATED DATE */}
                    <td style={{ textAlign: "center", fontFamily: "'IBM Plex Mono', monospace", fontSize: "12px", color: "var(--slate)" }}>
                      {p.createdAt ? new Date(p.createdAt).toLocaleDateString("en-IN") : "-"}
                    </td>

                    {/* ACTIONS */}
                    <td>
                      <div style={{ display: "flex", justifyContent: "center", gap: "8px" }}>
                        <button
                          onClick={() => handleOpenView(p)}
                          title="View Details"
                          style={{
                            padding: "6px 12px",
                            background: "var(--teal-soft)",
                            color: "var(--teal-deep)",
                            border: "none",
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontSize: "11px",
                            fontWeight: 700,
                            fontFamily: "'IBM Plex Mono', monospace",
                            textTransform: "uppercase"
                          }}
                        >
                          View
                        </button>
                        <button
                          onClick={() => handleOpenEdit(p)}
                          title="Edit Program"
                          style={{
                            padding: "6px 12px",
                            background: "#FEF3C7",
                            color: "#D97706",
                            border: "none",
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontSize: "11px",
                            fontWeight: 700,
                            fontFamily: "'IBM Plex Mono', monospace",
                            textTransform: "uppercase"
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(p._id)}
                          title="Delete Program"
                          style={{
                            padding: "6px 12px",
                            background: "var(--rose-soft)",
                            color: "var(--rose)",
                            border: "none",
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontSize: "11px",
                            fontWeight: 700,
                            fontFamily: "'IBM Plex Mono', monospace",
                            textTransform: "uppercase"
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <TablePagination
              component="div"
              count={projectList.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
            />
          </div>
        )}

        {/* MODAL DIALOG */}
        {open && (
          <div 
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 100,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(16,24,38,0.4)",
              backdropFilter: "blur(4px)"
            }}
          >
            <div className="modal-box" style={{ maxWidth: "500px" }}>
              <div className="modal-header">
                <span>
                  {modalMode === "view" ? "View Project Program" : modalMode === "edit" ? "Edit Project Program" : "Create Project Program"}
                </span>
                <button 
                  onClick={() => setOpen(false)}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "var(--slate)" }}
                >
                  <FaTimes />
                </button>
              </div>

              <form onSubmit={handleSave}>
                <div className="modal-body" style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                  
                  {/* Select/Input Project */}
                  <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                    <label style={{ fontSize: "12px", fontWeight: 700, color: "var(--slate)" }}>Select Project</label>
                    <select
                      value={selectedProject}
                      disabled={modalMode === "view"}
                      onChange={(e) => {
                        setSelectedProject(e.target.value);
                        setSelectedProgram("");
                        setCustomProgram("");
                      }}
                      className="input-field"
                      required
                    >
                      <option value="">Select project</option>
                      {Object.keys(staticProjects).map(name => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                      <option value="custom">+ Add Custom Project</option>
                    </select>
                  </div>

                  {selectedProject === "custom" && (
                    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                      <label style={{ fontSize: "12px", fontWeight: 700, color: "var(--slate)" }}>Custom Project Name</label>
                      <input
                        type="text"
                        placeholder="Enter new project name"
                        disabled={modalMode === "view"}
                        className="input-field"
                        value={customProject}
                        onChange={(e) => setCustomProject(e.target.value)}
                        required
                      />
                    </div>
                  )}

                  {/* Select/Input Program */}
                  {selectedProject && (
                    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                      <label style={{ fontSize: "12px", fontWeight: 700, color: "var(--slate)" }}>Select Program</label>
                      <select
                        value={selectedProgram}
                        disabled={modalMode === "view"}
                        onChange={(e) => setSelectedProgram(e.target.value)}
                        className="input-field"
                        required
                      >
                        <option value="">Select program</option>
                        {getAvailablePrograms().map(prog => (
                          <option key={prog} value={prog}>{prog}</option>
                        ))}
                        <option value="custom">+ Add Custom Program</option>
                      </select>
                    </div>
                  )}

                  {selectedProgram === "custom" && (
                    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                      <label style={{ fontSize: "12px", fontWeight: 700, color: "var(--slate)" }}>Custom Program Name</label>
                      <input
                        type="text"
                        placeholder="Enter new program name"
                        disabled={modalMode === "view"}
                        className="input-field"
                        value={customProgram}
                        onChange={(e) => setCustomProgram(e.target.value)}
                        required
                      />
                    </div>
                  )}

                  {/* Course Duration */}
                  <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                    <label style={{ fontSize: "12px", fontWeight: 700, color: "var(--slate)" }}>Course Duration</label>
                    <input
                      type="text"
                      placeholder="e.g., 1 Day, 30 Hours, 3 Months"
                      disabled={modalMode === "view"}
                      className="input-field"
                      value={courseDuration}
                      onChange={(e) => setCourseDuration(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    style={{
                      background: "var(--plum-soft)",
                      color: "var(--plum)",
                      border: "1px solid var(--hairline)",
                      borderRadius: "6px",
                      padding: "8px 16px",
                      cursor: "pointer",
                      fontSize: "12px",
                      fontWeight: 600
                    }}
                  >
                    {modalMode === "view" ? "Close" : "Cancel"}
                  </button>

                  {modalMode !== "view" && (
                    <button
                      type="submit"
                      disabled={saving}
                      style={{
                        background: "var(--brass)",
                        color: "#FFFFFF",
                        border: "none",
                        borderRadius: "6px",
                        padding: "8px 16px",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        gap: "6px"
                      }}
                    >
                      {saving && <FaSpinner className="animate-spin text-xs" />}
                      {modalMode === "edit" ? "Save Changes" : "Save Program"}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        )}

      </div>
    </>
  );
};

export default Project;
